// Hoàn tất lần đến: khách xem lại dịch vụ đã làm trong ngày → đánh giá → ký một lần (hoặc ký giấy / hẹn ký sau)
import { useEffect, useMemo, useState } from 'react';
import { Checkbox, Input, Modal, Rate, Segmented, Select, message } from 'antd';
import dayjs from 'dayjs';
import { CalendarCheck2, Camera, Printer } from 'lucide-react';
import { BRANCHES } from '../POS/data';
import usePosStore from '../POS/store';
import { useTreatmentAuth } from '../Treatment/actions';
import { compressImage, putMedia } from './mediaDb';
import { visitRecordId } from './logic';
import useC360Store from './store';
import SignaturePad from './SignaturePad';
import { printDoc, visitSections, visitTitle, type SignParty } from './templates';
import { signVisit, useVisits } from './visits';

export interface VisitTarget { posIds: string[]; customerKey?: string; date: string; party: SignParty }

type Mode = 'sign' | 'paper' | 'later';
const LATER_REASONS = ['Khách vội, hẹn ký lần sau', 'Máy tính bảng hỏng / hết pin', 'Khách cần xem lại với người nhà', 'Khác'];

export default function VisitSignModal({ target, onClose }: { target: VisitTarget | null; onClose: () => void }) {
  const visits = useVisits(target?.posIds || [], target?.customerKey);
  const upsert = useC360Store(s => s.upsertVisitRecord);
  const markPrinted = useC360Store(s => s.markPrinted);
  const signDays = useC360Store(s => s.config.visitSignDays);
  const workingBranch = usePosStore(s => s.workingBranch);
  const { userName } = useTreatmentAuth();
  const v = target ? visits.find(x => x.date === target.date) : undefined;

  const [mode, setMode] = useState<Mode>('sign');
  const [keys, setKeys] = useState<string[]>([]);
  const [rating, setRating] = useState(0);
  const [ratingNote, setRatingNote] = useState('');
  const [note, setNote] = useState('');
  const [agree, setAgree] = useState(false);
  const [witness, setWitness] = useState('');
  const [branchId, setBranchId] = useState(workingBranch);
  const [signature, setSignature] = useState<string | null>(null);
  const [paperFile, setPaperFile] = useState<File | null>(null);
  const [laterReason, setLaterReason] = useState(LATER_REASONS[0]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!target) return;
    setMode('sign');
    setKeys(v?.unsigned.map(i => i.key) || []);
    setRating(v?.rating || 0); setRatingNote(v?.ratingNote || ''); setNote('');
    setAgree(false); setWitness(userName); setBranchId(v?.branchIds[0] || workingBranch);
    setSignature(null); setPaperFile(null); setLaterReason(LATER_REASONS[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  const sections = useMemo(() => (v && target ? visitSections(target.party, v, keys, { note, rating, ratingNote }) : []), [v, target, keys, note, rating, ratingNote]);
  if (!target) return null;

  const late = target.date !== dayjs().format('YYYY-MM-DD');
  const recId = (posId?: string) => visitRecordId(posId || v?.posCustomerIds[0] || target.posIds[0], target.date);
  const ownerOf = (k: string) => v?.items.find(i => i.key === k)?.posCustomerId;
  const errors: string[] = [];
  if (!v) errors.push('Không tìm thấy lần đến');
  if (mode !== 'later' && !keys.length) errors.push('Chọn ít nhất một dịch vụ');
  if (mode === 'sign' && !agree) errors.push('Khách cần tick xác nhận');
  if (mode === 'sign' && !signature) errors.push('Khách chưa ký');

  const saveRating = () => {
    if (!rating || !v) return;
    const pid = ownerOf(keys[0]) || v.posCustomerIds[0];
    upsert(recId(pid), { posCustomerId: pid, date: target.date, rating, ratingNote: ratingNote.trim() || undefined });
  };

  const submit = async (andPrint = false) => {
    if (errors.length || !v) return;
    setSaving(true);
    try {
      if (mode === 'sign' && signature) {
        const doc = await signVisit({ posIds: target.posIds, customerKey: target.customerKey, date: target.date, keys, party: target.party, signature, witness: witness.trim() || userName, branchId, note: note.trim(), rating: rating || undefined, ratingNote: ratingNote.trim() || undefined });
        if (doc) {
          message.success(`Đã lưu phiếu xác nhận ${doc.id}`);
          if (andPrint) { printDoc(doc, signature); markPrinted(doc.id); }
        }
      } else if (mode === 'paper') {
        const pid = ownerOf(keys[0]) || v.posCustomerIds[0];
        const id = recId(pid);
        let photoId = v.paperPhotoId;
        if (paperFile) {
          photoId = `${id}:paper:${Date.now()}`;
          await putMedia(photoId, (await compressImage(paperFile, 1600, 0.8)).dataUrl);
        }
        const prev = v.records.find(r => r.id === id)?.paper?.keys || [];
        upsert(id, { posCustomerId: pid, date: target.date, deferred: undefined, paper: { at: new Date().toISOString(), by: userName, keys: Array.from(new Set([...prev, ...keys])), photoId } });
        saveRating();
        message.success('Đã ghi nhận khách ký phiếu giấy');
      } else {
        upsert(recId(), { posCustomerId: v.posCustomerIds[0], date: target.date, deferred: { at: new Date().toISOString(), by: userName, reason: laterReason } });
        saveRating();
        message.info('Đã hẹn ký sau – lần đến sẽ hiện nhắc ở lần khách quay lại');
      }
      onClose();
    } catch {
      message.error('Không lưu được – bộ nhớ trình duyệt bị chặn hoặc đầy');
    } finally {
      setSaving(false);
    }
  };

  const okLabel = mode === 'sign' ? 'Lưu chữ ký' : mode === 'paper' ? 'Ghi nhận ký giấy' : 'Hẹn ký sau';
  const signedOrPaper = (k: string) => v?.signedKeys.includes(k) ? 'Đã ký điện tử' : v?.paperKeys.includes(k) ? 'Đã ký giấy' : '';

  return (
    <Modal open centered width={760} destroyOnHidden onCancel={onClose}
      title={<span className="flex flex-wrap items-center gap-2"><CalendarCheck2 className="h-5 w-5 text-[#357f96]" /> Hoàn tất lần đến – {target.party.name}
        <span className="text-sm font-normal text-slate-500">{dayjs(target.date).format('DD/MM/YYYY')}{late ? ' · ký bổ sung' : ''}</span></span>}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-red-500">{errors[0] || ''}</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="h-9 rounded-lg border border-slate-300 px-4 text-sm font-semibold">Đóng</button>
            {mode === 'sign' && <button onClick={() => submit(true)} disabled={!!errors.length || saving} className="flex h-9 items-center gap-1.5 rounded-lg border border-[#357f96] px-4 text-sm font-semibold text-[#357f96] disabled:opacity-40"><Printer className="h-4 w-4" /> Lưu & in</button>}
            <button onClick={() => submit()} disabled={!!errors.length || saving} className="h-9 rounded-lg bg-[#357f96] px-4 text-sm font-semibold text-white hover:bg-[#2c6b7f] disabled:opacity-40">{okLabel}</button>
          </div>
        </div>
      }>
      {!v ? <div className="py-8 text-center text-slate-400">Không có dịch vụ nào trong ngày này</div> : (
        <div className="grid gap-3 text-sm">
          <div className="rounded-lg border border-slate-200">
            <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2 text-xs text-slate-500">
              <span>Dịch vụ trong lần đến · {dayjs(v.firstAt).format('HH:mm')}{v.lastAt !== v.firstAt ? `–${dayjs(v.lastAt).format('HH:mm')}` : ''} · KTV {v.staff.join(', ') || '—'}</span>
              {v.deferred && <span className="text-amber-700">Hẹn ký sau: {v.deferred.reason}</span>}
            </div>
            {v.items.map(i => {
              const done = signedOrPaper(i.key);
              return (
                <label key={i.key} className={`flex items-start gap-2 px-3 py-2 ${i.needSign && !done ? 'cursor-pointer hover:bg-slate-50' : 'opacity-60'}`}>
                  <Checkbox disabled={!i.needSign || !!done} checked={keys.includes(i.key)} className="mt-0.5"
                    onChange={e => setKeys(k => (e.target.checked ? [...k, i.key] : k.filter(x => x !== i.key)))} />
                  <span className="min-w-0 flex-1">
                    <span className="font-medium text-slate-800">{dayjs(i.at).format('HH:mm')} · {i.title}</span>
                    <span className="block text-xs text-slate-500">{i.detail}</span>
                  </span>
                  <span className="shrink-0 text-xs text-slate-500">{done || (!i.needSign ? 'Không cần ký' : '')}</span>
                </label>
              );
            })}
          </div>

          <div className="grid gap-3 sm:grid-cols-[auto_1fr] sm:items-center">
            <span className="font-medium text-slate-700">Khách đánh giá buổi làm</span>
            <div className="flex flex-wrap items-center gap-2">
              <Rate value={rating} onChange={setRating} />
              <Input className="min-w-[200px] flex-1" maxLength={200} value={ratingNote} onChange={e => setRatingNote(e.target.value)} placeholder="Góp ý của khách (không bắt buộc)" />
            </div>
          </div>
          <Input.TextArea rows={2} maxLength={300} value={note} onChange={e => setNote(e.target.value)} placeholder="Ghi chú sau buổi làm (tình trạng, dặn dò chăm sóc tại nhà…)" />

          <Segmented block value={mode} onChange={x => setMode(x as Mode)} options={[
            { value: 'sign', label: 'Khách ký trên máy' }, { value: 'paper', label: 'Khách ký giấy' }, { value: 'later', label: 'Hẹn ký sau' },
          ]} />

          {mode === 'sign' && <>
            <details className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <summary className="cursor-pointer font-semibold text-slate-700">Xem nội dung phiếu: {visitTitle(target.date)}</summary>
              <div className="mt-2 max-h-56 overflow-y-auto">
                {sections.map(s => (
                  <div key={s.heading} className="mb-2"><div className="font-semibold text-slate-700">{s.heading}</div>{s.lines.map((l, i) => <div key={i} className="text-slate-600">{l}</div>)}</div>
                ))}
              </div>
            </details>
            <Checkbox checked={agree} onChange={e => setAgree(e.target.checked)}><span className="font-medium">Tôi xác nhận đã được thực hiện các dịch vụ trên</span></Checkbox>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block"><span className="mb-1 block font-medium text-slate-700">Nhân viên chứng kiến</span><Input value={witness} onChange={e => setWitness(e.target.value)} /></label>
              <label className="block"><span className="mb-1 block font-medium text-slate-700">Ký tại</span><Select className="w-full" value={branchId} onChange={setBranchId} options={BRANCHES.map(b => ({ value: b.id, label: b.name }))} /></label>
            </div>
            <SignaturePad onChange={setSignature} />
          </>}

          {mode === 'paper' && (
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border-2 border-dashed border-slate-300 p-3 text-slate-600 hover:border-[#3e91ab]">
              <Camera className="h-5 w-5 shrink-0" />
              <span className="min-w-0 flex-1">{paperFile ? <b>{paperFile.name}</b> : 'Chụp ảnh phiếu giấy khách đã ký (nên có để đối chiếu)'}</span>
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => setPaperFile(e.target.files?.[0] || null)} />
            </label>
          )}

          {mode === 'later' && (
            <div className="space-y-2">
              <Select className="w-full" value={laterReason} onChange={setLaterReason} options={LATER_REASONS.map(r => ({ value: r, label: r }))} />
              <p className="text-xs text-slate-500">Lần đến giữ trạng thái "Chờ ký" và nhắc lại khi khách quay lại. Quá {signDays} ngày chưa ký sẽ hiện trong danh sách báo quản lý.</p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
